import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { BillIcon } from "components/icons/BillIcon";
import {
  BILL_STATUS,
  BILL_STATUS_LABEL,
  TAG_TYPE,
} from "constants/vendorBills";
import {
  useGetBillOrRequestQuery,
  useUpdateBillOrRequestMutation,
} from "store/apis/vendorBills";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { FilledGreenWiteTick } from "components/icons/FilledGreenWhiteTick";
import { useModal } from "hooks/useModal";
import { BillSlider } from "components/VendorBills/BillSlider";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { Divider } from "components/design/Divider";
import Loader from "components/DesignSystem/Loader/loader";

export const BillDraftCard = ({
  groupId,
  size,
  billId,
  entityId,
}: {
  groupId: string;
  size?: "regular" | "block";
  billId: string;
  entityId: string;
}) => {
  const { alertToast } = useToast();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const billSlider = useModal();

  const {
    data: bill,
    isLoading: isBillLoading,
    isFetching: isBillFetching,
  } = useGetBillOrRequestQuery(
    { groupId, entityId, billId },
    {
      skip: !billId || !entityId || !groupId,
    }
  );

  const [markActive, { isLoading: isMarkingActive }] =
    useUpdateBillOrRequestMutation();

  const status = bill?.bill_payment_status;

  const onAddBill = async () => {
    try {
      await markActive({
        billId,
        entityId,
        groupId,
        payload: { bill_payment_status: BILL_STATUS.DUE },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  if (isBillLoading || isBillFetching) {
    return (
      <div className="t-min-w-72 sm:t-min-w-0">
        <ChatCard.Root size={size} className="t-w-full">
          <div className="t-flex t-items-center t-justify-center t-h-64">
            <Loader size="small" />
          </div>
        </ChatCard.Root>
      </div>
    );
  }

  return (
    <div className="t-min-w-72 sm:t-min-w-0">
      <ChatCard.Root size={size} className="t-w-full">
        <ChatCard.Header>
          <div className="t-flex t-items-center t-min-w-64 t-justify-between ">
            <div className="t-items-center t-gap-1.5 t-flex">
              <BillIcon />
              {bill?.bill_payment_status === BILL_STATUS.DRAFT
                ? "Draft bill"
                : "Bill"}
            </div>

            <div>
              {bill?.bill_payment_status !== BILL_STATUS.DRAFT && status && (
                <Tag tagType={TAG_TYPE[status]} rounded icon={false}>
                  {BILL_STATUS_LABEL[status]}
                </Tag>
              )}
            </div>
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div>
            {bill?.entity_merchant_data?.name && (
              <div>
                <div className="t-text-subtext-sm t-text-text-30">
                  Vendor name
                </div>
                <div className="t-font-medium t-text-body-sm-italic">
                  {bill?.entity_merchant_data?.name}
                </div>
              </div>
            )}
            <div className="t-mt-3">
              <Divider type="dotted" />
            </div>
            {bill?.bill_date && (
              <div>
                <div className="t-text-subtext-sm t-text-text-30 t-mt-3">
                  Bill date
                </div>
                <div className="t-font-medium t-text-body-sm-italic">
                  {dayjs(bill?.bill_date).format(DISPLAY_DATE)}
                </div>
              </div>
            )}
            <div className="t-mt-3">
              <Divider type="dotted" />
            </div>
            <div className="t-font-semibold t-text-body-lg t-mt-5">
              <AmountSuperScript amount={Number(bill?.sub_total)} />
            </div>
            {bill?.bill_payment_status &&
              bill?.bill_payment_status !== BILL_STATUS.DRAFT && (
                <div className="t-flex t-text-body-sm t-text-text-30 t-items-center t-mt-3">
                  <FilledGreenWiteTick />
                  <div className="t-ml-1">Marked as active</div>
                </div>
              )}
          </div>
          {bill?.bill_payment_status === BILL_STATUS.DRAFT ? (
            <Button
              size="small"
              onClick={() =>
                history.push(link(`/books/bills-and-vendors/bill/${billId}`))
              }
            >
              View and edit
            </Button>
          ) : (
            <Button size="small" onClick={billSlider.open}>
              View
            </Button>
          )}
          {bill?.bill_payment_status &&
            bill?.bill_payment_status === BILL_STATUS.DRAFT && (
              <Button
                size="small"
                customType="primary"
                onClick={onAddBill}
                isLoading={isMarkingActive}
              >
                Mark as active
              </Button>
            )}
        </ChatCard.Body>
      </ChatCard.Root>

      <BillSlider
        isOpen={billSlider.isOpen}
        close={billSlider.close}
        billId={billId}
      />
    </div>
  );
};
